import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type AffilLine, type AffilRule, type AffilRuleKind, type AffilSettleResult } from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import { FitBox } from '../components/FitBox';
import { CustPicker } from '../components/RecvFields';
import { canScreen, useMe } from '../lib/me';
import { hitAny, searchWords } from '../lib/search';

/**
 * 회계 › 관계법인 정산 — 구글시트 「관계법인정산SYS」 이관(2026-11-03 way). 서버 `functions/src/affilSettle.ts`.
 * 방향 「A 청구매출 → B」 = 운행은 A 소속, 고객 청구는 B 법인 → A 가 B 에게 청구할 몫(영업계 합).
 * 순액 = 두 법인 사이를 맞비긴 금액(「지급 → 받음」). 고정청구는 따로 보이고 합계·순액엔 안 더한다(시트와 같다).
 * 규칙(인상률·제외 거래처·고정청구)은 「규칙」 보기에서 회계 입력 권한이 고친다 — 시트 수식에 박혀 있던 것.
 */

type View = 'sum' | 'lines' | 'excluded' | 'rules';
const KIND: Record<AffilRuleKind, string> = { markup: '인상', exclude: '제외 거래처', fixed: '고정청구' };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** 관계법인 정산은 지난달을 맞춘다(이번 달은 운행 중) — 기본 = 지난달. */
const lastMonth = () => { const d = new Date(Date.now() + 9 * 3600_000); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1); return d.toISOString().slice(0, 7); };
const th = 'whitespace-nowrap bg-white px-2 py-1.5';
const td = 'whitespace-nowrap px-2 py-1';
const pct = (rate: string | null) => (rate ? `×${Number(rate)}` : '');

export function AffilSettleTab() {
  const { me } = useMe();
  const canEdit = canScreen(me, 'accounting', 'write');
  const [mon, setMon] = useState(lastMonth());
  const [data, setData] = useState<AffilSettleResult | null>(null);
  const [view, setView] = useState<View>('sum');
  const [pair, setPair] = useState('');
  const [term, setTerm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(''); setData(null);
    try { setData(await api.getAffilSettle({ mon })); } catch (e) { setErr(errMsg(e)); }
  }, [mon]);
  useEffect(() => { void load(); }, [load]);

  const corps = data?.corps ?? [];
  const name = useMemo(() => new Map(corps.map((c) => [c.grp_id, c.label])), [data]);
  const amt = useMemo(() => new Map((data?.pairs ?? []).map((p) => [`${p.from_grp}>${p.to_grp}`, p])), [data]);
  const words = useMemo(() => searchWords(term.trim().toLowerCase()), [term]);
  const hit = (l: AffilLine) => hitAny(words, [l.cust_name, l.dispt_name, l.driver_name, l.car_no, l.recv_id, l.amt, name.get(l.from_grp), name.get(l.to_grp)]);
  const lines = useMemo(() => (data?.lines ?? []).filter((l) => !l.excluded && (!pair || `${l.from_grp}>${l.to_grp}` === pair) && hit(l)), [data, pair, words]);
  const excluded = useMemo(() => (data?.lines ?? []).filter((l) => l.excluded && hit(l)), [data, words]);
  const fixed = (data?.rules ?? []).filter((r) => r.kind === 'fixed' && r.active);
  const total = (data?.pairs ?? []).reduce((s, p) => s + Number(p.amount), 0);
  const pairLabel = (k: string) => { const [f, t] = k.split('>').map(Number); return `${name.get(f!)}청구매출 → ${name.get(t!)}`; };

  async function exportXlsx() {
    if (!data) return;
    setBusy(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      const sum: (string | number)[][] = [[`관계법인 청구매출 ${data.mon}`], [], ['운행 소속 ↓ · 청구법인 →', ...corps.map((c) => c.label), '합계']];
      for (const f of corps) {
        const row = corps.map((t) => (f.grp_id === t.grp_id ? '' : Number(amt.get(`${f.grp_id}>${t.grp_id}`)?.amount ?? 0)));
        sum.push([f.label, ...row, row.reduce<number>((s, v) => s + (typeof v === 'number' ? v : 0), 0)]);
      }
      sum.push([], ['순액(지급 → 받음)', '금액']);
      for (const n of data.nets) sum.push([`${name.get(n.payer)} → ${name.get(n.payee)}`, Number(n.amount)]);
      sum.push([], ['고정청구(순액에 안 더함)', '금액', '메모']);
      for (const r of fixed) sum.push([`${name.get(r.from_grp_id)} 청구 → ${name.get(r.to_grp_id!)}`, Number(r.amount), r.memo ?? '']);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sum), '요약');
      const head = ['소속', '청구법인', '고객명', '노선명', '기사', '차량', '운행시작', '청구상태', '영업계', '인상', '반영액', '접수NO'];
      const rowOf = (l: AffilLine) => [name.get(l.from_grp) ?? '', name.get(l.to_grp) ?? '', l.cust_name, l.dispt_name, l.driver_name, l.car_no,
        l.start_time, l.req_status, Number(l.base), l.rate ? Number(l.rate) : '', Number(l.amt), l.recv_id ?? ''];
      // 시트처럼 청구법인마다 한 장(그 법인이 받을 청구 = 다른 법인이 운행한 줄)
      for (const t of corps) {
        const ls = data.lines.filter((l) => !l.excluded && l.to_grp === t.grp_id);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...ls.map(rowOf)]), t.label);
      }
      const ex = data.lines.filter((l) => l.excluded);
      if (ex.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...ex.map(rowOf)]), '제외됨');
      XLSX.writeFile(wb, `관계법인정산_${data.mon}.xlsx`);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 text-sm">
        <input type="month" className="field w-36" aria-label="정산월" value={mon} onChange={(e) => e.target.value && setMon(e.target.value)} />
        {([['sum', '요약'], ['lines', `상세 ${data ? data.lines.filter((l) => !l.excluded).length : ''}`],
          ['excluded', `제외됨 ${data ? data.lines.filter((l) => l.excluded).length : ''}`], ['rules', `규칙 ${data ? data.rules.length : ''}`]] as [View, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
                  className={`rounded-lg px-2.5 py-1 ${view === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>{label}</button>
        ))}
        {(view === 'lines' || view === 'excluded') && <input className="field ml-auto w-56" placeholder="거래처·노선·기사·차량·금액" value={term} onChange={(e) => setTerm(e.target.value)} />}
        <button className={`btn-ghost ${view === 'lines' || view === 'excluded' ? '' : 'ml-auto'}`} disabled={!data || busy} onClick={() => void exportXlsx()}>엑셀 받기</button>
      </div>
      {data && (
        <p className="page-hint px-1">
          {data.mon} 관계법인 청구매출 {money(total)} · 기사 운행 줄 중 <b>운행 소속 ≠ 청구법인</b>인 관계법인 줄의 영업계(운행료 + 영업정산 기사 가감)를 더했습니다
          (청구상태가 있는 줄만). 칸을 누르면 그 방향 줄이 나옵니다. 「인상」 규칙이 있는 방향은 줄마다 영업계 × 율(원 단위 반올림).
        </p>
      )}
      {err && <ErrorBox message={err} />}
      {view === 'rules' ? (
        data && <RulesView data={data} canEdit={canEdit} onSaved={load} />
      ) : (
        <FitBox deps={[data, view, pair, term, err]}>
          {view === 'sum' && data && (
            <div className="space-y-4">
              <table className="text-sm">
                <thead className="sticky top-0">
                  <tr className="text-xs text-ink-soft">
                    <th className={`${th} text-left`}>운행 소속 ↓ · 청구법인 →</th>
                    {corps.map((c) => <th key={c.grp_id} className={`${th} text-right`}>{c.label}</th>)}
                    <th className={`${th} text-right`}>합계</th>
                  </tr>
                </thead>
                <tbody>
                  {corps.map((f) => {
                    const rowSum = corps.reduce((s, t) => s + Number(amt.get(`${f.grp_id}>${t.grp_id}`)?.amount ?? 0), 0);
                    return (
                      <tr key={f.grp_id} className="border-t border-line">
                        <td className={`${td} font-semibold`}>{f.label} 청구매출</td>
                        {corps.map((t) => {
                          if (f.grp_id === t.grp_id) return <td key={t.grp_id} className={`${td} bg-surface-soft text-center text-ink-faint`}>—</td>;
                          const k = `${f.grp_id}>${t.grp_id}`;
                          const p = amt.get(k);
                          const marked = (data.rules ?? []).some((r) => r.kind === 'markup' && r.active && r.from_grp_id === f.grp_id && r.to_grp_id === t.grp_id);
                          return (
                            <td key={t.grp_id} className={`${td} text-right`}>
                              {p && p.count > 0 ? (
                                <button className="text-brand-700 hover:underline" title={`${pairLabel(k)} ${p.count}줄${marked ? ' · 인상 적용' : ''}`}
                                        onClick={() => { setPair(k); setView('lines'); }}>
                                  {money(p.amount)}{marked && <span className="ml-0.5 text-[10px] text-amber-700">↑</span>}
                                </button>
                              ) : <span className="text-ink-faint">0</span>}
                            </td>
                          );
                        })}
                        <td className={`${td} text-right font-semibold`}>{money(rowSum)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex flex-wrap items-start gap-6">
                <table className="text-sm">
                  <thead><tr className="text-xs text-ink-soft"><th className={`${th} text-left`}>순액(지급 → 받음)</th><th className={`${th} text-right`}>금액</th></tr></thead>
                  <tbody>
                    {[...data.nets].sort((a, b) => Number(b.amount) - Number(a.amount)).map((n) => (
                      <tr key={`${n.payer}>${n.payee}`} className="border-t border-line">
                        <td className={td}>{name.get(n.payer)} → {name.get(n.payee)}</td>
                        <td className={`${td} text-right ${Number(n.amount) ? 'font-semibold' : 'text-ink-faint'}`}>{money(n.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <table className="text-sm">
                  <thead><tr className="text-xs text-ink-soft"><th className={`${th} text-left`}>고정청구(순액에 안 더함)</th><th className={`${th} text-right`}>금액</th><th className={`${th} text-left`}>메모</th></tr></thead>
                  <tbody>
                    {fixed.length === 0 && <tr><td colSpan={3} className="px-2 py-3 text-ink-faint">이 달 고정청구 없음</td></tr>}
                    {fixed.map((r) => (
                      <tr key={r.id} className="border-t border-line">
                        <td className={td}>{name.get(r.from_grp_id)} 청구 → {name.get(r.to_grp_id!)}</td>
                        <td className={`${td} text-right`}>{money(r.amount)}</td>
                        <td className={td}>{dash(r.memo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {(view === 'lines' || view === 'excluded') && (
            <>
              {view === 'lines' && (
                <div className="mb-1 flex items-center gap-1 text-sm">
                  <select className="field w-64" aria-label="방향" value={pair} onChange={(e) => setPair(e.target.value)}>
                    <option value="">모든 방향</option>
                    {(data?.pairs ?? []).filter((p) => p.count > 0).map((p) => {
                      const k = `${p.from_grp}>${p.to_grp}`;
                      return <option key={k} value={k}>{pairLabel(k)} ({p.count})</option>;
                    })}
                  </select>
                  <span className="text-xs text-ink-soft">{lines.length}줄 · 반영액 {money(lines.reduce((s, l) => s + Number(l.amt), 0))}</span>
                </div>
              )}
              {view === 'excluded' && <p className="page-hint mb-1 px-1">「제외 거래처」 규칙에 걸려 합계에서 뺀 줄입니다(고정청구로 따로 정산하는 거래처).</p>}
              <LineTable rows={view === 'lines' ? lines : excluded} name={name} loading={!data && !err} />
            </>
          )}
        </FitBox>
      )}
    </div>
  );
}

function LineTable({ rows, name, loading }: { rows: AffilLine[]; name: Map<number, string>; loading: boolean }) {
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0">
        <tr className="text-left text-xs text-ink-soft">
          {['소속 → 청구법인', '고객명', '노선명', '기사', '차량', '운행시작', '청구상태', '영업계', '인상', '반영액', '접수NO'].map((h) => <th key={h} className={th}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={11} className="px-2 py-6 text-center text-ink-faint">{loading ? '불러오는 중…' : '줄이 없습니다.'}</td></tr>}
        {rows.map((l, i) => (
          <tr key={i} className="border-t border-line">
            <td className={td}>{name.get(l.from_grp)} → {name.get(l.to_grp)}</td>
            <td className="max-w-[14rem] truncate px-2 py-1" title={l.cust_name}>{dash(l.cust_name)}</td>
            <td className="max-w-[18rem] truncate px-2 py-1" title={l.dispt_name}>{dash(l.dispt_name)}</td>
            <td className={td}>{dash(l.driver_name)}</td>
            <td className={td}>{dash(l.car_no)}</td>
            <td className={td}>{dash(l.start_time)}</td>
            <td className={td}>{dash(l.req_status)}</td>
            <td className={`${td} text-right`}>{money(l.base)}</td>
            <td className={`${td} text-xs text-amber-700`}>{pct(l.rate)}</td>
            <td className={`${td} text-right font-semibold`}>{money(l.amt)}</td>
            <td className={td}>{dash(l.recv_id)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type Draft = { id?: number; kind: AffilRuleKind; fromGrpId: string; toGrpId: string; custId: number | null; custName: string;
  rate: string; amount: string; memo: string; fromMon: string; toMon: string };
const blank = (mon: string): Draft => ({ kind: 'fixed', fromGrpId: '', toGrpId: '', custId: null, custName: '', rate: '1.1', amount: '', memo: '', fromMon: mon, toMon: '' });

function RulesView({ data, canEdit, onSaved }: { data: AffilSettleResult; canEdit: boolean; onSaved: () => void }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const name = new Map(data.corps.map((c) => [c.grp_id, c.label]));
  const set = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const edit = (r: AffilRule) => setDraft({ id: r.id, kind: r.kind, fromGrpId: String(r.from_grp_id), toGrpId: r.to_grp_id ? String(r.to_grp_id) : '',
    custId: r.cust_id, custName: r.cust_name ?? '', rate: r.rate ?? '1.1', amount: r.amount ?? '', memo: r.memo ?? '', fromMon: r.from_mon, toMon: r.to_mon ?? '' });

  async function save() {
    if (!draft) return;
    setBusy(true); setErr('');
    try {
      await api.saveAffilRule({ id: draft.id, kind: draft.kind, fromGrpId: Number(draft.fromGrpId), toGrpId: draft.toGrpId ? Number(draft.toGrpId) : null,
        custId: draft.custId, rate: draft.kind === 'markup' ? draft.rate : null, amount: draft.kind === 'fixed' ? draft.amount.replace(/,/g, '') : null,
        memo: draft.memo || null, fromMon: draft.fromMon, toMon: draft.toMon || null });
      setDraft(null); onSaved();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }
  async function off(r: AffilRule) {
    if (!window.confirm(`${KIND[r.kind]} 규칙을 끕니다(${name.get(r.from_grp_id)}${r.to_grp_id ? ' → ' + name.get(r.to_grp_id) : ''} ${r.cust_name ?? ''}). 끈 뒤엔 그 달부터 계산에서 빠집니다.`)) return;
    setBusy(true); setErr('');
    try { await api.saveAffilRule({ id: r.id, off: true }); onSaved(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2">
      <p className="page-hint px-1">
        <b>인상</b> = 그 방향 줄마다 영업계 × 율 · <b>제외 거래처</b> = 그 소속이 운행한 그 거래처 줄은 합계에서 뺌 · <b>고정청구</b> = 매달 같은 금액을 따로 청구(합계·순액엔 안 더함).
        기간은 시작월~종료월(빈칸 = 계속). 고치면 옛 규칙은 꺼지고 새 줄로 남습니다.
      </p>
      {err && <ErrorBox message={err} />}
      {canEdit && !draft && <button className="btn-primary" onClick={() => setDraft(blank(data.mon))}>새 규칙</button>}
      {draft && (
        <div className="card space-y-2 p-3 text-sm">
          <div className="flex flex-wrap items-end gap-2">
            <label className="space-y-0.5"><span className="block text-xs text-ink-soft">종류</span>
              <select className="field" value={draft.kind} onChange={(e) => set({ kind: e.target.value as AffilRuleKind })}>
                {(Object.keys(KIND) as AffilRuleKind[]).map((k) => <option key={k} value={k}>{KIND[k]}</option>)}
              </select>
            </label>
            <label className="space-y-0.5"><span className="block text-xs text-ink-soft">{draft.kind === 'fixed' ? '청구하는 법인' : '운행 소속'}</span>
              <select className="field" value={draft.fromGrpId} onChange={(e) => set({ fromGrpId: e.target.value })}>
                <option value="">— 고르기</option>
                {data.corps.map((c) => <option key={c.grp_id} value={c.grp_id}>{c.label}</option>)}
              </select>
            </label>
            {draft.kind !== 'exclude' && (
              <label className="space-y-0.5"><span className="block text-xs text-ink-soft">{draft.kind === 'fixed' ? '받는 법인' : '청구법인'}</span>
                <select className="field" value={draft.toGrpId} onChange={(e) => set({ toGrpId: e.target.value })}>
                  <option value="">— 고르기</option>
                  {data.corps.map((c) => <option key={c.grp_id} value={c.grp_id}>{c.label}</option>)}
                </select>
              </label>
            )}
            {draft.kind === 'markup' && (
              <label className="space-y-0.5"><span className="block text-xs text-ink-soft">율(10% 인상 = 1.1)</span>
                <input className="field w-24" value={draft.rate} onChange={(e) => set({ rate: e.target.value })} />
              </label>
            )}
            {draft.kind === 'fixed' && (
              <label className="space-y-0.5"><span className="block text-xs text-ink-soft">금액(원)</span>
                <input className="field w-36 text-right" inputMode="numeric" value={draft.amount}
                       onChange={(e) => set({ amount: e.target.value.replace(/[^0-9]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') })} />
              </label>
            )}
            <label className="space-y-0.5"><span className="block text-xs text-ink-soft">시작월</span>
              <input type="month" className="field w-36" value={draft.fromMon} onChange={(e) => set({ fromMon: e.target.value })} />
            </label>
            <label className="space-y-0.5"><span className="block text-xs text-ink-soft">종료월(빈칸 = 계속)</span>
              <input type="month" className="field w-36" value={draft.toMon} onChange={(e) => set({ toMon: e.target.value })} />
            </label>
          </div>
          {draft.kind !== 'markup' && (
            <div className="space-y-0.5">
              <span className="block text-xs text-ink-soft">거래처{draft.kind === 'exclude' ? '(필수)' : '(참고)'}{draft.custId ? ` — ${draft.custName} (NO ${draft.custId})` : ''}</span>
              <CustPicker value={draft.custId ?? undefined} onPick={(c) => set({ custId: c.cust_id, custName: c.cust_name ?? '' })} />
            </div>
          )}
          <label className="block space-y-0.5"><span className="block text-xs text-ink-soft">메모</span>
            <input className="field w-full" maxLength={200} value={draft.memo} onChange={(e) => set({ memo: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <button className="btn-primary" disabled={busy} onClick={() => void save()}>{draft.id ? '고쳐 저장' : '저장'}</button>
            <button className="btn-ghost" disabled={busy} onClick={() => setDraft(null)}>취소</button>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-ink-soft">
            {['종류', '법인', '거래처', '율·금액', '기간', '메모', `${data.mon} 적용`, '바꾼 사람', ''].map((h, i) => <th key={i} className={th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.rules.length === 0 && <tr><td colSpan={9} className="px-2 py-6 text-center text-ink-faint">규칙이 없습니다.</td></tr>}
          {data.rules.map((r) => (
            <tr key={r.id} className={`border-t border-line ${r.active ? '' : 'text-ink-faint'}`}>
              <td className={td}>{KIND[r.kind]}</td>
              <td className={td}>{name.get(r.from_grp_id) ?? r.from_grp_id}{r.to_grp_id ? ` → ${name.get(r.to_grp_id) ?? r.to_grp_id}` : ''}</td>
              <td className={td}>{dash(r.cust_name)}</td>
              <td className={`${td} text-right`}>{r.kind === 'markup' ? pct(r.rate) : r.kind === 'fixed' ? money(r.amount) : '—'}</td>
              <td className={td}>{r.from_mon} ~ {r.to_mon ?? ''}</td>
              <td className="max-w-[20rem] truncate px-2 py-1" title={r.memo ?? ''}>{dash(r.memo)}</td>
              <td className={td}>{r.active ? '적용' : '기간 밖'}</td>
              <td className={`${td} text-xs`} title={r.updated_at}>{dash(r.updated_by ?? r.created_by)}</td>
              <td className={td}>
                {canEdit && (
                  <span className="flex gap-1">
                    <button className="btn-ghost px-2 py-0.5 text-xs" disabled={busy} onClick={() => edit(r)}>고치기</button>
                    <button className="btn-ghost px-2 py-0.5 text-xs" disabled={busy} onClick={() => void off(r)}>끄기</button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
